import { Component } from '@theme/component';

/**
 * Manages the Care Protection checkbox on the product page.
 * When checked, injects hidden form fields with a unique token into all product forms.
 * When unchecked, removes those hidden fields.
 *
 * @typedef {object} CareProtectionRefs
 * @property {HTMLInputElement} checkbox - The warranty/protection checkbox
 * @property {HTMLElement} protectionProductId - Element with data-id for the protection variant
 * @extends Component<CareProtectionRefs>
 */
export class CareProtectionComponent extends Component {
  requiredRefs = ['checkbox'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.refs.checkbox.addEventListener('change', this.#onCheckboxChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get protectionVariantId() {
    return this.refs.protectionProductId?.dataset.id ?? '';
  }

  get isChecked() {
    return this.refs.checkbox.checked;
  }

  #onCheckboxChange = () => {
    const forms = document.querySelectorAll('product-form-component form');

    if (this.isChecked) {
      const token = Date.now().toString();
      const html = `
        <div class="orchard-protection-inputs">
          <input type="hidden" name="properties[_Care Protection No]" value="${token}">
          <input type="hidden" name="properties[_Care Protection Status]" value="Accepted">
        </div>
      `;
      for (const form of forms) {
        form.insertAdjacentHTML('afterbegin', html);
      }
    } else {
      document.querySelectorAll('.orchard-protection-inputs').forEach((el) => el.remove());
    }
  };

  /**
   * After the main product is added to cart, add the protection product
   * with a matching token.
   * @param {string} productTitle - Title of the product being protected
   * @returns {Promise<object>} The cart add response
   */
  async addProtectionToCart(productTitle) {
    const protectionWrapper = document.querySelector('.orchard-protection-inputs');
    if (!protectionWrapper) return null;

    const token = protectionWrapper.querySelector('[name="properties[_Care Protection No]"]')?.value;
    if (!token || !this.protectionVariantId) return null;

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: this.protectionVariantId,
        quantity: 1,
        properties: {
          '_Care Protection No': token,
          'Care Protection For': productTitle,
        },
      }),
    });

    return response.json();
  }
}

if (!customElements.get('care-protection-component')) {
  customElements.define('care-protection-component', CareProtectionComponent);
}
